import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { storage } from '../../data/localStorage';

const { FiX, FiCalendar, FiClock, FiUsers, FiBook, FiMapPin, FiAlertCircle } = FiIcons;

const AddExamModal = ({ isOpen, onClose, onSubmit, initialDate }) => {
  const [formData, setFormData] = useState({
    title: '',
    courseCode: '',
    classCode: '',
    date: '',
    time: '',
    duration: 120, // minutes
    expectedTakers: 30,
    venue: '',
    examType: 'Final Exam',
    instructions: '',
    materials: '',
    invigilators: [''],
    status: 'scheduled'
  });

  const [errors, setErrors] = useState({});
  const [availableCourses, setAvailableCourses] = useState([]);

  const examTypes = [
    'Final Exam',
    'Midterm Exam',
    'Quiz',
    'Assignment',
    'Practical Exam',
    'Oral Exam',
    'Project Presentation'
  ];

  useEffect(() => {
    if (isOpen) {
      // Load available course codes from questions
      const questions = storage.getQuestions();
      const courses = [...new Set(questions.map(q => q.courseCode).filter(Boolean))];
      setAvailableCourses(courses);
      
      // Set default date to tomorrow or use initialDate if provided
      if (initialDate) {
        setFormData(prev => ({
          ...prev,
          date: initialDate
        }));
      } else {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setFormData(prev => ({
          ...prev,
          date: tomorrow.toISOString().split('T')[0]
        }));
      }
      
      // Default time to 9:00 AM
      setFormData(prev => ({
        ...prev,
        time: '09:00'
      }));
    }
  }, [isOpen, initialDate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Exam title is required';
    }

    if (!formData.courseCode.trim()) {
      newErrors.courseCode = 'Course code is required';
    }

    if (!formData.classCode.trim()) {
      newErrors.classCode = 'Class code is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    } else {
      const examDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (examDate < today) {
        newErrors.date = 'Exam date cannot be in the past';
      }
    }

    if (!formData.time) {
      newErrors.time = 'Time is required';
    }

    if (!formData.venue.trim()) {
      newErrors.venue = 'Venue is required';
    }

    if (formData.expectedTakers < 1) {
      newErrors.expectedTakers = 'Expected takers must be at least 1';
    }

    if (formData.duration < 15) {
      newErrors.duration = 'Duration must be at least 15 minutes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Combine date and time
      const examDateTime = new Date(`${formData.date}T${formData.time}`);
      
      const examData = {
        ...formData,
        date: examDateTime.toISOString(),
        invigilators: formData.invigilators.filter(inv => inv.trim()),
        createdAt: new Date().toISOString()
      };

      onSubmit(examData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      courseCode: '',
      classCode: '',
      date: '',
      time: '',
      duration: 120,
      expectedTakers: 30,
      venue: '',
      examType: 'Final Exam',
      instructions: '',
      materials: '',
      invigilators: [''],
      status: 'scheduled'
    });
    setErrors({});
    onClose();
  };

  const addInvigilator = () => {
    setFormData(prev => ({
      ...prev,
      invigilators: [...prev.invigilators, '']
    }));
  };

  const removeInvigilator = (index) => {
    if (formData.invigilators.length > 1) {
      setFormData(prev => ({
        ...prev,
        invigilators: prev.invigilators.filter((_, i) => i !== index)
      }));
    }
  };

  const updateInvigilator = (index, value) => {
    setFormData(prev => ({
      ...prev,
      invigilators: prev.invigilators.map((inv, i) => i === index ? value : inv)
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCalendar} className="text-primary-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Schedule New Exam</h3>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exam Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Data Structures Final Exam"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exam Type
                  </label>
                  <select
                    value={formData.examType}
                    onChange={(e) => setFormData({ ...formData, examType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {examTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Code *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiBook} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData.courseCode}
                      onChange={(e) => setFormData({ ...formData, courseCode: e.target.value.toUpperCase() })}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.courseCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., CS101, MATH201"
                      list="course-codes"
                    />
                    <datalist id="course-codes">
                      {availableCourses.map(code => (
                        <option key={code} value={code} />
                      ))}
                    </datalist>
                  </div>
                  {errors.courseCode && (
                    <p className="text-red-500 text-sm mt-1">{errors.courseCode}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Class Code *
                  </label>
                  <input
                    type="text"
                    value={formData.classCode}
                    onChange={(e) => setFormData({ ...formData, classCode: e.target.value.toUpperCase() })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.classCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., A1, B2, LAB1"
                  />
                  {errors.classCode && (
                    <p className="text-red-500 text-sm mt-1">{errors.classCode}</p>
                  )}
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.date ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.date && (
                    <p className="text-red-500 text-sm mt-1">{errors.date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiClock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.time ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.time && (
                    <p className="text-red-500 text-sm mt-1">{errors.time}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (minutes) *
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="480"
                    step="15"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.duration ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.duration && (
                    <p className="text-red-500 text-sm mt-1">{errors.duration}</p>
                  )}
                </div>
              </div>

              {/* Venue and Participants */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Venue *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMapPin} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={formData.venue}
                      onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.venue ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                      placeholder="e.g., Room 101, Lab A, Hall B"
                    />
                  </div>
                  {errors.venue && (
                    <p className="text-red-500 text-sm mt-1">{errors.venue}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Takers *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiUsers} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={formData.expectedTakers}
                      onChange={(e) => setFormData({ ...formData, expectedTakers: parseInt(e.target.value) })}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.expectedTakers ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.expectedTakers && (
                    <p className="text-red-500 text-sm mt-1">{errors.expectedTakers}</p>
                  )}
                </div>
              </div>

              {/* Invigilators */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invigilators
                </label>
                <div className="space-y-2">
                  {formData.invigilators.map((invigilator, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={invigilator}
                        onChange={(e) => updateInvigilator(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Invigilator name"
                      />
                      {formData.invigilators.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInvigilator(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <SafeIcon icon={FiX} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addInvigilator}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    + Add Invigilator
                  </button>
                </div>
              </div>

              {/* Instructions and Materials */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Special Instructions
                  </label>
                  <textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Any special instructions for students or invigilators..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Required Materials
                  </label>
                  <textarea
                    value={formData.materials}
                    onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Calculator, ruler, formula sheet, etc."
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  type="submit"
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiCalendar} />
                  <span>Schedule Exam</span>
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddExamModal;